import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Discounts } from '@prisma/client';
import isEqual from 'lodash.isequal';
import { getInitiatePaymentPayload } from '../../helpers/payments';
import { Appointment } from '../../interfaces';
import { CheckoutFormValues } from '../../types/appointments';
import useUser from '../global/useUser';
import { getCheckoutData } from '../../services/payments';
import useDebouncedValue from '../useDebouncedValue';
import useCheckoutFormState from './useCheckoutFormState';

function useGetCheckoutData({
  appointment,
  values,
  discount,
}: {
  appointment: Appointment | null;
  values: CheckoutFormValues;
  discount: Discounts | null;
}) {
  const { user } = useUser();
  const businessId = (appointment?.businessId || user?.business?.id)!;
  const { dispatch } = useCheckoutFormState();

  const debouncedValues = useDebouncedValue(values, 500);

  const {
    data,
    isFetching,
    isLoading,
    isError,
    isSuccess,
    error,
    refetch,
    isPlaceholderData,
  } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      'checkout-data',
      businessId,
      appointment?.appointmentId,
      debouncedValues,
      discount?.id,
    ],
    queryFn: async () => {
      if (
        !debouncedValues.price &&
        !debouncedValues.customOrderItems?.some(
          item => item.price > 0 && item.quantity > 0,
        )
      )
        return null;

      const { data: payload } = getInitiatePaymentPayload({
        appointmentId: appointment?.appointmentId,
        businessId,
        values: {
          ...debouncedValues,
          paymentMethod: debouncedValues.paymentMethod || 'CASH',
        },
        discount,
      });

      if (!payload.appointment && !payload.order) return null;

      const response = await getCheckoutData(payload);

      return response;
    },
    gcTime: 30000,
    staleTime: 30000,
    structuralSharing: false,
  });

  const isDebouncing = React.useMemo(
    () => !isEqual(values, debouncedValues),
    [values, debouncedValues],
  );
  React.useEffect(() => {
    dispatch({ type: 'SET_DEBOUNCING', payload: isDebouncing });

    return () => {
      dispatch({ type: 'SET_DEBOUNCING', payload: false });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDebouncing]);

  return {
    data,
    isPlaceholderData,
    isFetching,
    isLoading,
    isDebouncing,
    isError,
    isSuccess,
    error,
    refetch,
  };
}

export default useGetCheckoutData;
